import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import isEqual from "lodash/isEqual";
import { connect } from "react-redux";
import { API_URL } from "../../../config";
import { Link, useParams, Navigate, useLocation } from "react-router-dom";
import { Container, Form, Table, Button, Nav, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Icon } from "react-icons-kit";
import { ic_arrow_back, ic_delete_outline } from "react-icons-kit/md";
import MainWrapper from "../../MainWrapper";
import Title from "../../elements/Title";
import CustomButton from "../../elements/CustomButton";
import { toast } from "react-toastify";
import {
  CC_ESTIMATE_SAVE_REQUEST,
  CC_ESTIMATE_LOAD_REQUEST,
  CC_SCOPES_REQUEST,
} from "../../../_utils/constants/CountertopCabinetEstimate";
import { CUSTOMER_LIST_REQUEST } from "../../../_utils/constants/Customer";
import { STORE_INFO_REQUEST } from "../../../_utils/constants/admin/StoreInfo";
import { PRODUCT_TYPE_REQUEST } from "../../../_utils/constants/ProductCatalog";
import { buildEstimatePayload, hydrateEstimate } from "../../../_utils/manualEstimatePayload";
import { deriveCountertopCabinetPermissions } from "../../../_utils/countertopCabinetPermissions";
import { isCountertopCabinetStore } from "../../../_utils/countertopCabinet";
import AddScopeModal from "./models/AddScopeModal";
import AddLaborItemModal from "./models/AddLaborItemModal";
import ConvertToOrderModal from "./models/ConvertToOrderModal";
import { formatCurrency, groupByStructure } from "../../../_utils/manualQuantityScopes";
import { assignScopeBlocks } from "../../../_utils/addScopeItems";
import {
  recalcLinePrice,
  lineExtended,
  sectionTotals,
  estimateItemsTotal,
  validateEstimateItems,
  hasEstimateItems,
  upsertUnique,
  materialIdentityKey,
  laborIdentityKey,
  syncKitAddonQuantity,
  syncDerivedFromEaAddon,
} from "../../../_utils/manualEstimateItems";

// ---------------------------------------------------------------------------
// Presentational form (no Redux / no Router) — exported for straightforward testing.
//
// The estimate is a flat list of MATERIAL and LABOR line items in the OrderConfirmation shape
// (quantity, unit, privateName/style/color, unitPrice, linePrice, gpm, lineCost, laborCategory,
// labor, ...). Rows are pulled in via the purpose-built "Add Scope" modal — the parent opens it
// through the injected onAddScope callback (pick a C/C structure -> check products + hand-key price
// -> kit items) and feeds the returned rows back in through incomingItems (append by token).
// Everything is manually editable; no graphical/floorplan/geometry logic lives here.
// ---------------------------------------------------------------------------
export const ScopeEstimateForm = ({
  initialMaterial = [],
  initialLabor = [],
  isSaving = false,
  isDeleting = false,
  isEditMode = false,
  onSave,
  onAddScope,
  onConvertToOrder,
  onDelete,
  incomingItems = { material: [], labor: [], token: 0 },
  canEdit = true,
  showCurrentCost = false,
  structureNameMap = {},
  canConvertToOrder = false,
  defaultScopeName = "",
  storeId = 0,
  contactInfoId = "",
  // Saved estimate photo (GetCountertopCabinetEstimate scopePhotoUrl).
  initialScopePhotoUrl = "",
}) => {
  const readOnly = !canEdit;
  // Per-scope "Add New Material/Labor Item" — the scope group each modal targets (null when closed).
  const [addMaterialTarget, setAddMaterialTarget] = useState(null);
  const [addLaborTarget, setAddLaborTarget] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // The scope card pending deletion (null when the confirm dialog is closed).
  const [scopeToDelete, setScopeToDelete] = useState(null);
  const [materialList, setMaterialList] = useState(
    Array.isArray(initialMaterial) ? initialMaterial : []
  );
  const [laborList, setLaborList] = useState(Array.isArray(initialLabor) ? initialLabor : []);
  const [errors, setErrors] = useState(null);
  // Single scope name for this estimate (stored on every item for API round-trip). A new estimate
  // starts pre-filled with a default name (e.g. "Estimate 2"); a loaded estimate uses its saved name.
  const [scopeName, setScopeName] = useState(
    initialMaterial?.[0]?.scopeName || initialLabor?.[0]?.scopeName || defaultScopeName || ""
  );
  const photoInputRefs = useRef({});
  // Estimate photo shown next to "Estimate Name"; sent as scopePhotoUrl on Save / Update (a new pick is
  // a base64 data URL, like the per-scope room photos).
  const [scopePhotoUrl, setScopePhotoUrl] = useState(initialScopePhotoUrl || "");
  const estimatePhotoInputRef = useRef(null);
  // Full-size preview for any thumbnail on the page (estimate photo, room photos) — same modal as
  // CncOrderDetail's photo preview.
  const [previewPhoto, setPreviewPhoto] = useState(null);
  // Clickable / keyboard-accessible thumbnail props.
  const previewProps = (src, label) => ({
    role: "button",
    tabIndex: 0,
    title: "Click to preview",
    "aria-label": `Preview ${label}`,
    onClick: () => setPreviewPhoto(src),
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setPreviewPhoto(src);
      }
    },
  });

  // Auto-hide the top-level validation banner (e.g. "add at least one item") a few seconds after
  // it appears, so it doesn't linger indefinitely once the estimator has read it. Per-line field
  // errors (isInvalid on the inputs) are untouched — only the banner clears.
  useEffect(() => {
    if (!errors?.items) return undefined;
    const timer = setTimeout(() => {
      setErrors((prev) => (prev ? { ...prev, items: "" } : prev));
    }, 5000);
    return () => clearTimeout(timer);
  }, [errors?.items]);

  // Append rows merged in from the "Add Scope" modal whenever the parent bumps the token.
  const lastIncomingToken = useRef(incomingItems?.token || 0);
  useEffect(() => {
    const token = incomingItems?.token || 0;
    if (token && token !== lastIncomingToken.current) {
      lastIncomingToken.current = token;
      const newMaterial = Array.isArray(incomingItems.material) ? incomingItems.material : [];
      const newLabor = Array.isArray(incomingItems.labor) ? incomingItems.labor : [];
      // The same structure/product can be picked in a later Add Scope run; de-dupe by identity and
      // let the latest selection replace the existing line (in place) instead of duplicating it.
      if (newMaterial.length)
        setMaterialList((prev) => upsertUnique(prev, newMaterial, materialIdentityKey));
      if (newLabor.length) setLaborList((prev) => upsertUnique(prev, newLabor, laborIdentityKey));
    }
  }, [incomingItems]);

  // Scope identity used to merge "Add New Material/Labor Item" rows into THIS scope (not a new one).
  const groupMeta = (group) => {
    const first = (group?.material?.[0]) || (group?.labor?.[0]) || {};
    // The block's primary product — used to anchor added cross-structure rows (see stampForScope).
    const primary = (group?.material || []).find((m) => m.isPrimary === true) || {};
    return {
      scopeBlockId: group?.scopeBlockId ?? first.scopeBlockId ?? null,
      productStructureId: Number(first.productStructureId ?? 0) || 0,
      primaryProductId: Number(primary.productId ?? 0) || 0,
      structureName: group?.structureName || first.structureName || "",
      roomName: group?.roomName || first.roomName || "",
      photoUrl: group?.photoUrl || first.photoUrl || "",
      scopeName: group?.scopeName || first.scopeName || "",
    };
  };
  // Attach added rows to the target SCOPE BLOCK (scopeBlockId + its room/photo/name), but keep each
  // row's OWN product structure. A scope block can hold several product structures, so a Cabinet
  // Boxes item added while on a Countertops scope must keep productStructureId/structureName =
  // Cabinet Boxes — that's the structure recorded in the estimate payload and carried to the measure.
  //
  // Also anchor standalone added rows (not a primary, no existing kit parent) to the block's primary
  // via parentProductId. The backend may drop the per-row scope#; this anchor lets reconstructScope-
  // Blocks reunite a cross-structure added row with its block on reload. parentProductName is left
  // blank so the grid's "belongs to" column stays clean (the row's structure differs from the primary).
  const stampForScope = (meta) => (item) => {
    const keepOwnParent = item.isPrimary === true || Number(item.parentProductId ?? 0) > 0;
    const anchor = keepOwnParent ? item.parentProductId : (meta.primaryProductId || item.parentProductId);
    return {
      ...item,
      scopeBlockId: meta.scopeBlockId,
      scopeProductStructureId: meta.productStructureId,
      structureName: item.structureName || meta.structureName,
      productStructureId: Number(item.productStructureId ?? 0) || meta.productStructureId,
      ...(anchor != null ? { parentProductId: anchor } : {}),
      roomName: meta.roomName,
      photoUrl: meta.photoUrl,
      scopeName: meta.scopeName,
    };
  };

  // Content-based key for a kit addon item — used to detect same physical item across different
  // parentProductIds (a kit shared by several primaries should appear only once in the grid).
  const kitMatBaseKey = (sbid, item) => [
    Number(sbid ?? 0),
    Number(item.productStructureId ?? 0),
    Number(item.materialId ?? 0),
    Number(item.productId ?? 0),
    String(item.privateName ?? "").toLowerCase(),
    String(item.style ?? "").toLowerCase(),
    String(item.color ?? "").toLowerCase(),
  ].join("|");

  const kitLaborBaseKey = (sbid, item) => [
    Number(sbid ?? 0),
    Number(item.productStructureId ?? 0),
    Number(item.laborItemId ?? item.laborId ?? 0),
    String(item.labor ?? item.laborName ?? "").toLowerCase(),
    String(item.laborCategory ?? "").toLowerCase(),
  ].join("|");

  const handleAddMaterialToScope = ({ material = [], labor = [] } = {}) => {
    const meta = groupMeta(addMaterialTarget);
    const stamp = stampForScope(meta);
    const sbid = meta.scopeBlockId;

    // Deduplicate kit addon items: if the same physical item already exists in this scope block
    // for any primary, skip the duplicate rather than adding a second row for a new primary.
    // Use kitId > 0 (not parentProductId) for materials — backend preserves kitId but drops
    // parentProductId on round-trip. For labor, include all existing rows because kitId may
    // also be 0 for labor after backend load.
    const existingKitMatKeys = new Set(
      materialList
        .filter((item) => Number(item.kitId) > 0 && Number(item.scopeBlockId) === Number(sbid))
        .map((item) => kitMatBaseKey(sbid, item))
    );
    const existingKitLaborKeys = new Set(
      laborList
        .filter((item) => Number(item.scopeBlockId) === Number(sbid))
        .map((item) => kitLaborBaseKey(sbid, item))
    );

    const dedupedMaterial = material.filter((item) => {
      if (!Number(item.parentProductId)) return true; // primary / standalone — always include
      return !existingKitMatKeys.has(kitMatBaseKey(sbid, item));
    });
    const dedupedLabor = labor.filter((item) => {
      if (!Number(item.parentProductId)) return true;
      return !existingKitLaborKeys.has(kitLaborBaseKey(sbid, item));
    });

    if (dedupedMaterial.length)
      setMaterialList((prev) => upsertUnique(prev, dedupedMaterial.map(stamp), materialIdentityKey));
    if (dedupedLabor.length)
      setLaborList((prev) => upsertUnique(prev, dedupedLabor.map(stamp), laborIdentityKey));
    setAddMaterialTarget(null);
  };

  const handleAddLaborToScope = (labor = []) => {
    const meta = groupMeta(addLaborTarget);
    const stamp = stampForScope(meta);
    const sbid = meta.scopeBlockId;

    const existingKitLaborKeys = new Set(
      laborList
        .filter((item) => Number(item.scopeBlockId) === Number(sbid))
        .map((item) => kitLaborBaseKey(sbid, item))
    );
    const dedupedLabor = labor.filter((item) => {
      if (!Number(item.parentProductId)) return true;
      return !existingKitLaborKeys.has(kitLaborBaseKey(sbid, item));
    });

    if (dedupedLabor.length)
      setLaborList((prev) => upsertUnique(prev, dedupedLabor.map(stamp), laborIdentityKey));
    setAddLaborTarget(null);
  };

  // Edit a single line's field and recompute its line price (qty x unit price).
  const editLine = (setList, index, field, value) =>
    setList((prev) =>
      prev.map((item, i) => (i === index ? recalcLinePrice({ ...item, [field]: value }) : item))
    );
  // Quantity can't be negative — clamp any negative entry to 0.
  const clampQty = (field, value) =>
    field === "quantity" && String(value).trim() !== "" && Number(value) < 0 ? "0" : value;

  // Update a single material field while typing — no kit sync yet.
  const handleMaterialChange = (index, field, value) =>
    editLine(setMaterialList, index, field, clampQty(field, value));

  // Run kit-addon quantity sync after the user commits a qty (Tab / click away).
  // Also used directly for non-qty fields (unit price) where no sync is needed.
  const handleMaterialField = (index, field, value) => {
    const val = clampQty(field, value);
    const current = materialList[index];
    const updatedMaterial = materialList.map((item, i) =>
      i === index ? recalcLinePrice({ ...item, [field]: val }) : item
    );
    // Reliable primary detection: explicit flag OR legacy heuristic (materialId=0, productId>0,
    // no parentProductId) — covers items reloaded from the backend where isPrimary is not persisted.
    const isCncPrimary = (item) =>
      item.isPrimary === true ||
      (item.isPrimary == null &&
        Number(item.materialId) === 0 &&
        Number(item.productId) > 0 &&
        !Number(item.parentProductId));
    if (field === "quantity" && isCncPrimary(current) && Number(current.kitId) > 0) {
      const scopeBlock = current.scopeBlockId ?? null;
      // CNC shared kit: kit addons belong to the canonical (first) primary only.
      // Sum all primaries sharing this kitId+scopeBlock so the kit qty reflects every primary.
      const allPrimaries = updatedMaterial.filter(
        (item) =>
          isCncPrimary(item) &&
          Number(item.kitId) === Number(current.kitId) &&
          (scopeBlock !== null ? Number(item.scopeBlockId) === Number(scopeBlock) : true)
      );
      const totalQty = allPrimaries.length > 1
        ? allPrimaries.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)
        : val;
      // Find the canonical parentProductId from existing kit addon rows so we target the right items.
      const addonWithParent = [...updatedMaterial, ...laborList].find(
        (item) =>
          !isCncPrimary(item) &&
          Number(item.kitId) === Number(current.kitId) &&
          (scopeBlock !== null ? Number(item.scopeBlockId) === Number(scopeBlock) : true) &&
          Number(item.parentProductId) > 0
      );
      const canonicalParentId = addonWithParent ? addonWithParent.parentProductId : current.productId;
      // For SF multi-primary (Cabinet Doors), pass individual primary qtys so syncKitAddonQuantity
      // can compute sum(ceil(each/cov)) instead of ceil(sum/cov) per rule 3a.
      const primaryQtysArr =
        allPrimaries.length > 1 && String(current.unit ?? "").toUpperCase() === "SF"
          ? allPrimaries.map((item) => Number(item.quantity) || 0)
          : null;
      const synced = syncKitAddonQuantity(
        updatedMaterial,
        laborList,
        current.kitId,
        totalQty,
        current.unit,
        canonicalParentId,
        scopeBlock,
        primaryQtysArr
      );
      // syncKitAddonQuantity's LF-rounding pass updates the canonical primary's qty to
      // roundedLF(totalQty). When multiple primaries share a kit each must keep its own
      // individually entered qty — restore them from updatedMaterial (kit addons stay updated).
      // Also apply per-primary LF rounding to the current item (syncKitAddonQuantity only rounds
      // the canonical keyed primary, leaving non-keyed siblings at their raw entered value).
      if (allPrimaries.length > 1) {
        const primaryById = new Map(
          updatedMaterial
            .filter(isCncPrimary)
            .map((item) => [String(item.productId), item])
        );
        // Round the current item's qty individually (same ceiling logic as syncKitAddonQuantity).
        if (String(current.unit ?? "").toUpperCase() === "LF") {
          const cov = Number(current.coverage) || 0;
          const roundedQty = cov > 0 ? Math.ceil(Number(val) / cov) * cov : Number(val);
          const base = primaryById.get(String(current.productId));
          if (base) {
            primaryById.set(
              String(current.productId),
              recalcLinePrice({ ...base, quantity: String(roundedQty) })
            );
          }
        }
        setMaterialList(
          synced.material.map((item) =>
            isCncPrimary(item) && primaryById.has(String(item.productId))
              ? primaryById.get(String(item.productId))
              : item
          )
        );
      } else {
        setMaterialList(synced.material);
      }
      setLaborList(synced.labor);
    } else if (
      field === "quantity" &&
      !current?.isPrimary &&
      Number(current.kitId) > 0 &&
      String(current.unit ?? "").trim().toUpperCase() === "EA" &&
      Number(current.coverage) > 0
    ) {
      // Resolve the edited addon's OWN primary (by parentProductId when present) so the cascade
      // stays within this primary's kit rows.
      const primaryItem = updatedMaterial.find((item) =>
        item.isPrimary &&
        Number(item.scopeBlockId || 0) === Number(current.scopeBlockId || 0) &&
        (current.parentProductId
          ? Number(item.productId) === Number(current.parentProductId)
          : Number(item.kitId) === Number(current.kitId))
      );
      const isSFMaster = String(primaryItem?.unit ?? "").trim().toUpperCase() === "SF";
      if (!isSFMaster) {
        const synced = syncDerivedFromEaAddon(
          updatedMaterial,
          laborList,
          current.kitId,
          val,
          current.parentProductId,
          current.scopeBlockId ?? null
        );
        setMaterialList(synced.material);
        setLaborList(synced.labor);
      } else {
        setMaterialList(updatedMaterial);
      }
    } else {
      setMaterialList(updatedMaterial);
    }
  };
  const handleLaborField = (index, field, value) =>
    editLine(setLaborList, index, field, clampQty(field, value));
  const handleDeleteMaterial = (index) =>
    setMaterialList((prev) => prev.filter((_, i) => i !== index));
  const handleDeleteLabor = (index) => setLaborList((prev) => prev.filter((_, i) => i !== index));


  // True when an item belongs to the given scope card. A real scope block (positive scopeBlockId)
  // owns ALL its rows regardless of product structure (match on scopeBlockId alone); legacy rows
  // (block 0 / none) still match on scopeBlockId + structureName (or structureName only).
  const itemInGroup = (item, group) => {
    const iname = item.structureName || structureNameMap[item.productStructureId] || "";
    if (group.scopeBlockId != null && Number(group.scopeBlockId) > 0) {
      return Number(item.scopeBlockId) === Number(group.scopeBlockId);
    }
    if (group.scopeBlockId != null) {
      return item.scopeBlockId === group.scopeBlockId && iname === group.structureName;
    }
    return iname === group.structureName;
  };

  const handleGroupMetaChange = (group, field, value) => {
    const update = (list) =>
      list.map((item) => (itemInGroup(item, group) ? { ...item, [field]: value } : item));
    setMaterialList(update);
    setLaborList(update);
  };

  // Remove a whole scope card (all its material + labor rows) from the form. Local only — the
  // user clicks Update to persist, same as Add Scope adds rows then saves.
  const handleDeleteScopeCard = (group) => {
    setMaterialList((prev) => prev.filter((item) => !itemInGroup(item, group)));
    setLaborList((prev) => prev.filter((item) => !itemInGroup(item, group)));
    setScopeToDelete(null);
  };

  const gpmText = (n) => `${(Number.isFinite(n) ? n : 0).toFixed(2)}%`;
  const showLinePrice = (item) =>
    formatCurrency(String(item.linePrice ?? "").trim() !== "" ? item.linePrice : lineExtended(item));
  const showCurrentCostValue = (item) => formatCurrency(item.unitCost ?? item.currentCost);

  const withName = (items) =>
    items.map((item) => ({
      ...item,
      structureName:
        item.structureName || structureNameMap[item.productStructureId] || "",
    }));
  const groups = groupByStructure(withName(materialList), withName(laborList));

  // Update the scope name on every item (API round-trip requires it on each line).
  const handleScopeNameChange = (value) => {
    setScopeName(value);
    const update = (list) => list.map((item) => ({ ...item, scopeName: value }));
    setMaterialList(update);
    setLaborList(update);
  };

  const handleConvertToOrderClick = () => {
    const errs = {};
    errs.scopeName = !String(scopeName || "").trim() ? "Scope name is required" : "";

    const groupErrs = {};
    groups.forEach((group) => {
      const gk =
        group.scopeBlockId != null
          ? `${group.scopeBlockId}_${group.structureName}`
          : group.structureName || "__other__";
      const ge = {};
      if (!String(group.roomName || "").trim()) ge.roomName = "Room name is required";
      if (!group.photoUrl) ge.photoUrl = "Photo is required";
      if (Object.keys(ge).length) groupErrs[gk] = ge;
    });
    errs.groups = groupErrs;

    const hasScopeError = !!errs.scopeName;
    const hasGroupErrors = Object.keys(groupErrs).length > 0;

    if (hasScopeError || hasGroupErrors) {
      setErrors((prev) => ({ ...(prev || {}), ...errs }));
      if (hasScopeError) toast.error("Scope name is required before converting to order.");
      if (Object.values(groupErrs).some((e) => e.roomName))
        toast.error("Room name is required for all sections before converting to order.");
      if (Object.values(groupErrs).some((e) => e.photoUrl))
        toast.error("Photo is required for all sections before converting to order.");
      return;
    }

    if (onConvertToOrder) onConvertToOrder();
  };

  const validate = () => {
    if (!hasEstimateItems(materialList, laborList)) {
      return {
        material: {},
        labor: {},
        items: "Add at least one material item before saving.",
        scopes: {},
        groups: {},
        isValid: false,
      };
    }
    const items = validateEstimateItems(materialList, laborList);
    const result = { material: items.material, labor: items.labor };
    result.items = !items.isValid ? "Enter a valid quantity and price for every line item." : "";
    result.scopeName = !String(scopeName || "").trim() ? "Scope name is required" : "";

    const groupErrs = {};
    groups.forEach((group) => {
      const gk =
        group.scopeBlockId != null
          ? `${group.scopeBlockId}_${group.structureName}`
          : group.structureName || "__other__";
      const errs = {};
      if (!String(group.roomName || "").trim()) errs.roomName = "Room name is required";
      if (!group.photoUrl) errs.photoUrl = "Photo is required";
      if (Object.keys(errs).length) groupErrs[gk] = errs;
    });

    result.groups = groupErrs;
    result.isValid = items.isValid && !result.scopeName && !Object.keys(groupErrs).length;
    return result;
  };

  const handleSave = (evt) => {
    evt.preventDefault();
    const result = validate();
    setErrors(result);
    if (!result.isValid) return;
    if (!onSave) return;
    // Give EVERY scope card a stable, distinct scopeBlockId before saving so two scopes of the same
    // product structure (e.g. two "Cabinet Boxes") persist as separate blocks instead of merging on
    // reload. Cards that already have an id keep it; cards that still lack one — the original scope of
    // an estimate that was saved before scope blocks were tracked, whose items come back with no
    // scopeBlockId — get fresh ids above the current max, so no two cards ever collide. The API must
    // persist this scopeBlockId on every material/labor row (and in nameList) for the split to survive.
    const existingIds = groups.map((g) => g.scopeBlockId).filter((id) => id != null);
    let nextBlockId = existingIds.length ? Math.max(...existingIds) : 0;
    const matOut = materialList.slice();
    const labOut = laborList.slice();
    groups.forEach((group) => {
      const sbid = group.scopeBlockId != null ? group.scopeBlockId : (nextBlockId += 1);
      group.material.forEach(({ absIdx }) => { matOut[absIdx] = { ...matOut[absIdx], scopeBlockId: sbid }; });
      group.labor.forEach(({ absIdx }) => { labOut[absIdx] = { ...labOut[absIdx], scopeBlockId: sbid }; });
    });
    onSave({
      materialList: matOut,
      laborList: labOut,
      scopeName,
      scopePhotoUrl,
      total: estimateItemsTotal(matOut, labOut),
    });
  };

  const hasUnsavedChanges = !isEqual(materialList, initialMaterial) ||
    !isEqual(laborList, initialLabor) ||
    (scopePhotoUrl || "") !== (initialScopePhotoUrl || "");

  const convertToOrderButton = isEditMode && onConvertToOrder ? (
    <OverlayTrigger
      placement="top"
      overlay={
        hasUnsavedChanges ? (
          <Tooltip>Update the scope to save your changes before converting to an order.</Tooltip>
        ) : !canConvertToOrder ? (
          <Tooltip>Save scope name, room name, and photo for all sections first.</Tooltip>
        ) : <span />
      }
    >
      <span>
        <Button
          type="button"
          style={{ backgroundColor: "#2D831F", borderColor: "#2D831F" }}
          onClick={handleConvertToOrderClick}
          disabled={!canConvertToOrder || hasUnsavedChanges || isSaving}
        >
          Convert to Measure
        </Button>
      </span>
    </OverlayTrigger>
  ) : null;

  return (
    <Form noValidate onSubmit={handleSave}>
      {!readOnly && onAddScope && isEditMode ? (
        <Button type="button" variant="success" className="mb-3" onClick={onAddScope} disabled={isSaving}>
          + Add Scope
        </Button>
      ) : null}
      {!readOnly && groups.length > 0 ? (
        <div className="mb-3">
          <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
            <div className="d-flex align-items-center flex-grow-1" style={{ maxWidth: "360px" }}>
              <Form.Label className="fw-semibold mb-0">Estimate Name</Form.Label>
              <OverlayTrigger placement="top" overlay={<Tooltip>Delete Estimate</Tooltip>}>
                <Button
                  variant="link"
                  type="button"
                  className="text-danger p-0 ms-auto"
                  aria-label="Delete estimate"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <Icon size={20} icon={ic_delete_outline} />
                </Button>
              </OverlayTrigger>
            </div>
            {/* Save / Update next to Convert to Measure (same submit as the button at the bottom). */}
            <div className="ms-auto d-flex align-items-center gap-2">
              <CustomButton variant="primary" type="submit" disabled={isSaving}>
                {isSaving ? "Saving..." : isEditMode ? "Update" : "Save"}
              </CustomButton>
              {convertToOrderButton}
            </div>
          </div>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <Form.Control
              type="text"
              style={{ maxWidth: "360px" }}
              placeholder="Estimate Name"
              aria-label="Scope name"
              value={scopeName}
              isInvalid={!!errors?.scopeName}
              onChange={(e) => handleScopeNameChange(e.target.value)}
            />
            {/* Estimate photo — saved with the estimate as scopePhotoUrl on Save / Update. */}
            <div className="d-flex align-items-center gap-2" data-testid="estimate-photo">
              {scopePhotoUrl ? (
                <>
                  <img
                    src={scopePhotoUrl}
                    alt="Estimate"
                    style={{ width: "80px", height: "60px", objectFit: "cover", borderRadius: "4px", cursor: "pointer" }}
                    {...previewProps(scopePhotoUrl, "estimate photo")}
                  />
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="p-0"
                    style={{ color: "#2D831F", textDecoration: "none" }}
                    onClick={() => estimatePhotoInputRef.current?.click()}
                  >
                    Change
                  </Button>
                </>
              ) : (
                <Button
                  type="button"
                  variant="success"
                  size="sm"
                  onClick={() => estimatePhotoInputRef.current?.click()}
                >
                  Upload Estimate Photo
                </Button>
              )}
              <input
                ref={estimatePhotoInputRef}
                type="file"
                accept="image/*"
                aria-label="Estimate photo"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;
                  if (!String(file.type || "").startsWith("image/")) {
                    toast.error("Please choose an image file.");
                    return;
                  }
                  const reader = new FileReader();
                  reader.onload = (ev) => setScopePhotoUrl(ev.target.result);
                  reader.readAsDataURL(file);
                }}
              />
            </div>
          </div>
          {errors?.scopeName ? (
            <div className="text-danger small mt-1">{errors.scopeName}</div>
          ) : null}
          {isEditMode && hasUnsavedChanges ? (
            <Form.Text className="d-block">Update the scope to save your changes before converting to an order.</Form.Text>
          ) : null}
        </div>
      ) : null}

      {groups.length === 0 ? (
        <>
          <p className="text-muted">No material items. Use &quot;Add Scope&quot; to pull in products.</p>
          <p className="text-muted">No labor items. Use &quot;Add Scope&quot; to pull in labor.</p>
        </>
      ) : (
        <>
          {groups.map((group) => {
              const matTotals = sectionTotals(group.material);
              const labTotals = sectionTotals(group.labor);
          const groupTotal = Math.round((matTotals.linePriceTotal + labTotals.linePriceTotal) * 100) / 100;
          const gk = group.scopeBlockId != null
            ? `${group.scopeBlockId}_${group.structureName}`
            : group.structureName || "__other__";

          return (
            <div key={gk} className="border rounded mb-4 p-3" style={{ borderTopLeftRadius: 0 }}>
              <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                <h5 style={{ color: "#2D831F" }} className="mb-0">
                  {group.structureName}
                </h5>
                <div className="d-flex align-items-center gap-2 ms-5">
                  <Form.Label className="small fw-semibold mb-0">Room Name</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    style={{ width: "160px" }}
                    placeholder="Room Name"
                    aria-label={`Room name for ${group.structureName}`}
                    value={group.roomName}
                    disabled={readOnly}
                    isInvalid={!!errors?.groups?.[gk]?.roomName}
                    onChange={(e) => handleGroupMetaChange(group, "roomName", e.target.value)}
                  />
                  {errors?.groups?.[gk]?.roomName ? (
                    <div className="text-danger small mt-1">{errors.groups[gk].roomName}</div>
                  ) : null}
                </div>
                {!readOnly ? (
                  <div>
                    {group.photoUrl ? (
                      <>
                        <img
                          src={group.photoUrl}
                          alt="scope"
                          style={{ width: "80px", height: "60px", objectFit: "cover", display: "block", marginBottom: "4px", cursor: "pointer" }}
                          {...previewProps(group.photoUrl, `${group.roomName || group.structureName || "room"} photo`)}
                        />
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          className="p-0"
                          style={{ color: "#2D831F", textDecoration: "none" }}
                          onClick={() => photoInputRefs.current[gk]?.click()}
                        >
                          Change
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          type="button"
                          variant="success"
                          size="sm"
                          onClick={() => photoInputRefs.current[gk]?.click()}
                        >
                          Insert Photo
                        </Button>
                        {errors?.groups?.[gk]?.photoUrl ? (
                          <div className="text-danger small mt-1">{errors.groups[gk].photoUrl}</div>
                        ) : null}
                      </>
                    )}
                    <input
                      ref={(el) => { photoInputRefs.current[gk] = el; }}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = (ev) =>
                          handleGroupMetaChange(group, "photoUrl", ev.target.result);
                        reader.readAsDataURL(file);
                        e.target.value = "";
                      }}
                    />
                  </div>
                ) : null}
                <span className="fw-bold ms-auto">
                  {group.structureName} Total: ${formatCurrency(groupTotal)}
                </span>
                {!readOnly ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Delete Scope</Tooltip>}
                  >
                    <Button
                      variant="link"
                      type="button"
                      className="text-danger p-0"
                      aria-label={`Delete scope ${group.structureName}`}
                      onClick={() => setScopeToDelete(group)}
                    >
                      <Icon size={20} icon={ic_delete_outline} />
                    </Button>
                  </OverlayTrigger>
                ) : null}
              </div>

              {group.material.length > 0 && (
              <>
              <h6 className="mt-2 mb-1">Material</h6>
              <Table bordered responsive className="mb-1">
                <thead>
                  <tr>
                    <th style={{ width: "110px" }}>Qty</th>
                    <th>Unit</th>
                    <th>Private Name</th>
                    <th>Style</th>
                    <th>Color</th>
                    {showCurrentCost ? <th style={{ width: "120px" }}>Current Cost</th> : null}
                    <th style={{ width: "120px" }}>Unit Price</th>
                    <th style={{ width: "120px" }}>Line Price</th>
                    {!readOnly ? <th style={{ width: "70px" }}>Actions</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {group.material.map(({ absIdx, ...item }) => (
                      <tr key={absIdx}>
                        <td>
                          <Form.Control
                            type="number"
                            step="0.01"
                            min="0"
                            aria-label={`Material ${absIdx + 1} quantity`}
                            value={item.quantity ?? ""}
                            disabled={readOnly}
                            isInvalid={!!errors?.material?.[absIdx]?.quantity}
                            onChange={(e) => {
                              if (item.isPrimary && String(item.unit ?? "").trim().toUpperCase() === "LF") {
                                handleMaterialChange(absIdx, "quantity", e.target.value);
                              } else {
                                handleMaterialField(absIdx, "quantity", e.target.value);
                              }
                            }}
                            onBlur={(e) => {
                              if (item.isPrimary && String(item.unit ?? "").trim().toUpperCase() === "LF") {
                                handleMaterialField(absIdx, "quantity", e.target.value);
                              }
                            }}
                          />
                          {errors?.material?.[absIdx]?.quantity ? (
                            <div className="text-danger small mt-1">{errors.material[absIdx].quantity}</div>
                          ) : null}
                        </td>
                        <td>{item.unit || item.unitName || ""}</td>
                        <td>
                          {item.privateName || ""}
                          {!item.isPrimary && item.parentProductName ? (
                            <div className="text-muted small fst-italic">for {item.parentProductName}</div>
                          ) : null}
                        </td>
                        <td>{item.style || item.product || ""}</td>
                        <td>{item.color || ""}</td>
                        {showCurrentCost ? (
                          <td data-testid={`material-current-cost-${absIdx}`}>${showCurrentCostValue(item)}</td>
                        ) : null}
                        <td>
                          <Form.Control
                            type="number"
                            step="0.01"
                            aria-label={`Material ${absIdx + 1} unit price`}
                            value={item.unitPrice ?? item.price ?? ""}
                            disabled={readOnly}
                            isInvalid={!!errors?.material?.[absIdx]?.unitPrice}
                            onChange={(e) => handleMaterialField(absIdx, "unitPrice", e.target.value)}
                          />
                          {errors?.material?.[absIdx]?.unitPrice ? (
                            <div className="text-danger small mt-1">{errors.material[absIdx].unitPrice}</div>
                          ) : null}
                        </td>
                        <td data-testid={`material-line-${absIdx}`}>${showLinePrice(item)}</td>
                        {!readOnly ? (
                          <td>
                            <Button
                              variant="link"
                              type="button"
                              className="text-danger p-0"
                              aria-label={`Delete material ${absIdx + 1}`}
                              onClick={() => handleDeleteMaterial(absIdx)}
                            >
                              <Icon size={20} icon={ic_delete_outline} />
                            </Button>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  <tr>
                    <td colSpan={showCurrentCost ? 6 : 5} className="text-end fw-bold">Total</td>
                    <td className="fw-bold">${formatCurrency(matTotals.unitPriceTotal)}</td>
                    <td className="fw-bold">${formatCurrency(matTotals.linePriceTotal)}</td>
                    {!readOnly ? <td></td> : null}
                  </tr>
                </tbody>
              </Table>
              <div className="d-flex flex-wrap justify-content-between gap-3 mb-2 fw-bold bg-light p-2 rounded">
                <span>Material Cost: ${formatCurrency(matTotals.cost)}</span>
                <span>Material Profit: ${formatCurrency(matTotals.profit)}</span>
                <span>Material GPM: {gpmText(matTotals.gpm)}</span>
              </div>
              </>
              )}
              {!readOnly && isEditMode ? (
                <div className="text-center mb-3">
                  <Button variant="success" type="button" disabled={isSaving} onClick={() => setAddMaterialTarget(group)}>
                    Add New Material Item
                  </Button>
                </div>
              ) : null}
              <>
              <h6 className="mt-3 mb-1">Labor</h6>
              <Table bordered responsive className="mb-1">
                <thead>
                  <tr>
                    <th style={{ width: "110px" }}>Qty</th>
                    <th>Unit</th>
                    <th>Labor Category</th>
                    <th>Labor Name</th>
                    {showCurrentCost ? <th style={{ width: "120px" }}>Current Cost</th> : null}
                    <th style={{ width: "120px" }}>Unit Price</th>
                    <th style={{ width: "120px" }}>Line Price</th>
                    {!readOnly ? <th style={{ width: "70px" }}>Actions</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {group.labor.map(({ absIdx, ...item }) => (
                      <tr key={absIdx}>
                        <td>
                          <Form.Control
                            type="number"
                            step="0.01"
                            min="0"
                            aria-label={`Labor ${absIdx + 1} quantity`}
                            value={item.quantity ?? ""}
                            disabled={readOnly}
                            isInvalid={!!errors?.labor?.[absIdx]?.quantity}
                            onChange={(e) => handleLaborField(absIdx, "quantity", e.target.value)}
                          />
                          {errors?.labor?.[absIdx]?.quantity ? (
                            <div className="text-danger small mt-1">{errors.labor[absIdx].quantity}</div>
                          ) : null}
                        </td>
                        <td>{item.unit || item.unitName || ""}</td>
                        <td>{item.laborCategory || ""}</td>
                        <td>
                          {item.labor || item.laborName || ""}
                          {item.parentProductName ? (
                            <div className="text-muted small fst-italic">for {item.parentProductName}</div>
                          ) : null}
                        </td>
                        {showCurrentCost ? (
                          <td data-testid={`labor-current-cost-${absIdx}`}>${showCurrentCostValue(item)}</td>
                        ) : null}
                        <td>
                          <Form.Control
                            type="number"
                            step="0.01"
                            aria-label={`Labor ${absIdx + 1} unit price`}
                            value={item.unitPrice ?? item.price ?? ""}
                            disabled={readOnly}
                            isInvalid={!!errors?.labor?.[absIdx]?.unitPrice}
                            onChange={(e) => handleLaborField(absIdx, "unitPrice", e.target.value)}
                          />
                          {errors?.labor?.[absIdx]?.unitPrice ? (
                            <div className="text-danger small mt-1">{errors.labor[absIdx].unitPrice}</div>
                          ) : null}
                        </td>
                        <td data-testid={`labor-line-${absIdx}`}>${showLinePrice(item)}</td>
                        {!readOnly ? (
                          <td>
                            <Button
                              variant="link"
                              type="button"
                              className="text-danger p-0"
                              aria-label={`Delete labor ${absIdx + 1}`}
                              onClick={() => handleDeleteLabor(absIdx)}
                            >
                              <Icon size={20} icon={ic_delete_outline} />
                            </Button>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  <tr>
                    <td colSpan={showCurrentCost ? 5 : 4} className="text-end fw-bold">Total</td>
                    <td className="fw-bold">${formatCurrency(labTotals.unitPriceTotal)}</td>
                    <td className="fw-bold">${formatCurrency(labTotals.linePriceTotal)}</td>
                    {!readOnly ? <td></td> : null}
                  </tr>
                </tbody>
              </Table>
              <div className="d-flex flex-wrap justify-content-between gap-3 mb-2 fw-bold bg-light p-2 rounded">
                <span>Labor Cost: ${formatCurrency(labTotals.cost)}</span>
                <span>Labor Profit: ${formatCurrency(labTotals.profit)}</span>
                <span>Labor GPM: {gpmText(labTotals.gpm)}</span>
              </div>
              {!readOnly && isEditMode ? (
                <div className="text-center mb-3">
                  <Button variant="success" type="button" disabled={isSaving} onClick={() => setAddLaborTarget(group)}>
                    Add New Labor Item
                  </Button>
                </div>
              ) : null}
              </>
            </div>
          );
        })}
        </>
      )}

      {groups.length > 0 ? (
        <>
          <div className="d-flex justify-content-end align-items-center mb-3">
            <span className="me-2">Estimate Total:</span>
            <strong data-testid="estimate-total">
              ${formatCurrency(estimateItemsTotal(materialList, laborList))}
            </strong>
          </div>

          {errors?.items ? (
            <div className="alert alert-danger fw-bold text-center" role="alert" data-testid="items-error">
              {errors.items}
            </div>
          ) : null}

          <div className="mt-4 d-flex flex-wrap align-items-end gap-2">
            {!readOnly ? (
              <CustomButton variant="primary" type="submit" disabled={isSaving}>
                {isSaving ? "Saving..." : isEditMode ? "Update" : "Save"}
              </CustomButton>
            ) : (
              <span className="text-muted">You do not have permission to edit this estimate.</span>
            )}
            {convertToOrderButton}
          </div>
        </>
      ) : null}
      {/* Photo preview modal (estimate photo / room photos) */}
      <Modal show={!!previewPhoto} onHide={() => setPreviewPhoto(null)} centered size="lg">
        <Modal.Header closeButton />
        <Modal.Body className="text-center p-2">
          {previewPhoto && (
            <img src={previewPhoto} alt="Preview" style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain" }} />
          )}
        </Modal.Body>
      </Modal>

      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Estimate</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this estimate? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={isDeleting} onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={isDeleting}
            onClick={() => { setShowDeleteConfirm(false); onDelete && onDelete(); }}
          >
            {isDeleting ? (
              <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Deleting…</>
            ) : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={!!scopeToDelete} onHide={() => setScopeToDelete(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Scope</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the {scopeToDelete?.structureName}
          {scopeToDelete?.roomName ? ` (${scopeToDelete.roomName})` : ""} scope? Update the estimate
          afterward to save this change.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setScopeToDelete(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => handleDeleteScopeCard(scopeToDelete)}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {addMaterialTarget ? (
        <AddScopeModal
          show={!!addMaterialTarget}
          onClose={() => setAddMaterialTarget(null)}
          onAdd={handleAddMaterialToScope}
          storeId={storeId}
          contactInfoId={contactInfoId}
          initialCheckedStructures={[groupMeta(addMaterialTarget).productStructureId]}
          title="Add Material Item"
          excludeProductIds={new Set(
            (addMaterialTarget.material || [])
              .filter((item) => !Number(item.parentProductId))
              .map((item) => Number(item.productId))
              .filter(Boolean)
          )}
          excludeKitMaterialIds={new Set(
            (addMaterialTarget.material || [])
              .filter((item) => Number(item.parentProductId) > 0)
              .map((item) => Number(item.materialId || item.productId))
              .filter(Boolean)
          )}
          excludeKitLaborIds={new Set(
            (addMaterialTarget.labor || [])
              .map((item) => Number(item.laborItemId || item.laborId))
              .filter(Boolean)
          )}
        />
      ) : null}
      {addLaborTarget ? (
        <AddLaborItemModal
          show={!!addLaborTarget}
          onClose={() => setAddLaborTarget(null)}
          onAdd={handleAddLaborToScope}
          storeId={storeId}
          contactInfoId={contactInfoId}
          productStructureId={groupMeta(addLaborTarget).productStructureId}
          structureName={groupMeta(addLaborTarget).structureName}
          excludeLaborIds={new Set(
            (addLaborTarget.labor || [])
              .map((item) => Number(item.laborItemId || item.laborId))
              .filter(Boolean)
          )}
        />
      ) : null}
    </Form>
  );
};

// ---------------------------------------------------------------------------
// Connected page — Countertops/Cabinets manual quantity calculator route target.
//
// Renders the Material/Labor grids + Save, fetches the customer (for storeId), and opens
// the purpose-built AddScopeModal. The modal returns { material, labor } (Qty blank) which is merged
// into the grids via the `incoming` token; the estimator keys quantities and Saves (draft).
// ---------------------------------------------------------------------------
export const CountertopCabinetQuantity = ({
  auth: { auth, isAuthenticated },
  countertopCabinetEstimate,
  customerList = [],
  storeInfo = [],
  ProductType,
  canView = true,
  canEdit = true,
  scopes = [],
  isScopesLoaded = false,
  isScopesLoading = false,
  isEstimateLoading = false,
  saveEstimate,
  loadEstimate,
  fetchScopes,
  fetchCustomerList,
  fetchStoreInfo,
  fetchProductType,
}) => {
  const { contactInfoId, unitTypeId, variantId } = useParams();
  const location = useLocation();
  // const hideEstimate = true;
  const { estimate, isSaving, isSaved, isLoaded, errors } = countertopCabinetEstimate || {};

  const estimateIdRef = useRef(0);
  const cnCMeasureQuantityIdRef = useRef(0);
  // After INSERT save, stores the cnCMeasureQuantityId from the save response so isScopesLoaded
  // can activate and load that specific scope tab.
  const afterInsertCnCIdRef = useRef(0);
  const prevSavingRef = useRef(isSaving);
  // True when the in-flight save was an INSERT (estimateId was 0 at dispatch time).
  const wasInsertRef = useRef(false);
  // What to do when the post-save fetchScopes refresh triggers isScopesLoaded:
  //   'insert' → find and activate the next empty scope
  //   'update' → skip (form already correct after reload)
  //   null     → initial page load → activate first scope
  const afterSaveActionRef = useRef(null);
  // Drives the create / edit mode label and Save vs Update button.
  const [activeEstimateId, setActiveEstimateId] = useState(0);
  // The customer's store (fetched by contactInfoId) anchors both the modal's store-scoped product
  // filtering and the estimate payload. A loaded estimate's own storeId takes precedence.
  const customerStoreId = customerList?.[0]?.storeId ?? 0;
  const storeId = estimate?.result?.storeId ?? estimate?.storeId ?? customerStoreId ?? 0;

  // Current Cost is shown in the Material/Labor grids only for Countertops/Cabinets customers —
  // detected via their store (isCountertopCabinet / storeTypeId === 2), same as CustomerDetail.
  const isCountertopCabinetCustomer = isCountertopCabinetStore(storeId, storeInfo);

  // Header labels: the customer detail page passes the property + floor-plan NAMES via route state
  // when opening this page. Fall back to the fetched customer's property name / the ids on refresh.
  const propertyName =
    location.state?.propertyName || customerList?.[0]?.shipToPropertyName || `customer #${contactInfoId}`;
  const floorPlanName = location.state?.floorPlanName || `#${unitTypeId}`;

  const [formKey, setFormKey] = useState(0);
  const [initialForm, setInitialForm] = useState({ material: [], labor: [] });

  // Active scope tab — keyed by cnCMeasureQuantityId (string), or "__new__" when a new estimate
  // is being built but not yet saved. Initialised to the first scope once the API result arrives.
  const [activeScopeKey, setActiveScopeKey] = useState(null);
  // True while the user is building a new (unsaved) estimate — shows a "New Estimate" temp tab.
  // Cleared when the estimate is saved and a real scope tab takes its place.
  const [pendingTab, setPendingTab] = useState(false);
  // Tracks the variantId of whichever top-level scope tab is currently active so handleSave uses
  // the correct variantId (the URL param never changes when the user switches tabs).
  const activeScopeVariantIdRef = useRef(variantId || 0);

  // "Add Scope" modal state + the rows it feeds back into the grids (bumped token = new merge).
  const [addScopeOpen, setAddScopeOpen] = useState(false);
  // Which flow opened the picker — drives its header title ("Add Estimate" vs "Add Scope").
  const [addScopeMode, setAddScopeMode] = useState("estimate");
  const [incoming, setIncoming] = useState({ material: [], labor: [], token: 0 });
  const incomingTokenRef = useRef(0);
  const scopeBlockIdCounterRef = useRef(0);

  const hasRequiredParams = Boolean(contactInfoId) && Boolean(unitTypeId);

  // On mount: load the customer (for storeId), available scopes, any saved estimate, and the C/C
  // structure catalog (ProductType) so items can be re-grouped by name after a reload even if
  // structureName is not persisted by the API.
  useEffect(() => {
    if (isAuthenticated && auth?.[0]?.token) {
      if (contactInfoId) {
        fetchCustomerList(auth[0].token, "asc", 1, 1, { contactInfoId }, auth[0].userId, false, false, false);
      }
      fetchStoreInfo(auth[0].token);
      if (!(ProductType?.valueList && ProductType.valueList.length)) {
        fetchProductType(auth[0].token);
      }
      if (hasRequiredParams) {
        fetchScopes(auth[0].token, contactInfoId, unitTypeId);
        loadEstimate(auth[0].token, contactInfoId, unitTypeId, variantId || 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Handles three scenarios when scopes data arrives / refreshes:
  //   null   (initial load)  → activate first scope and load its estimate
  //   'update' (after save)  → skip; form was already reloaded by the save-success handler
  //   'delete' (after delete) → reset to first remaining scope, or clear form if none left
  //   'insert' (after save)  → activate the next scope with no estimate (estimateId=0/null)
  useEffect(() => {
    if (!isScopesLoaded || !auth?.[0]?.token) return;
    const action = afterSaveActionRef.current;
    afterSaveActionRef.current = null;

    if (action === 'update') return; // form already up-to-date

    if (action === 'delete') {
      // Must run before the !scopes.length guard — deleting the last estimate leaves scopes empty
      // and we still need to reset the form.
      setPendingTab(false);
      const first = scopes[0] || null;
      if (first) {
        activeScopeVariantIdRef.current = first.variantId || 0;
        cnCMeasureQuantityIdRef.current = first.cnCMeasureQuantityId || 0;
        estimateIdRef.current = first.estimateId || 0;
        setActiveEstimateId(first.estimateId || 0);
        setActiveScopeKey(String(first.cnCMeasureQuantityId));
        setFormKey((k) => k + 1);
        setInitialForm({ material: [], labor: [] });
        if (first.estimateId) {
          loadEstimate(auth[0].token, contactInfoId, unitTypeId, first.variantId || 0, first.cnCMeasureQuantityId || 0);
        }
      } else {
        activeScopeVariantIdRef.current = 0;
        cnCMeasureQuantityIdRef.current = 0;
        estimateIdRef.current = 0;
        setActiveEstimateId(0);
        setActiveScopeKey(null);
        setFormKey((k) => k + 1);
        setInitialForm({ material: [], labor: [] });
      }
      return;
    }

    if (!scopes.length) return; // initial load or insert with no scopes yet — nothing to activate

    if (action === 'insert') {
      // After adding a new estimate, load and activate the FIRST scope tab so the user always lands
      // on a populated tab instead of a blank, no-tab-selected state.
      afterInsertCnCIdRef.current = 0;
      const first = scopes[0] || null;
      if (first) {
        activeScopeVariantIdRef.current = first.variantId || 0;
        cnCMeasureQuantityIdRef.current = first.cnCMeasureQuantityId || 0;
        estimateIdRef.current = first.estimateId || 0;
        setActiveEstimateId(first.estimateId || 0);
        setActiveScopeKey(String(first.cnCMeasureQuantityId));
        setFormKey((k) => k + 1);
        setInitialForm({ material: [], labor: [] });
        if (first.estimateId) {
          loadEstimate(auth[0].token, contactInfoId, unitTypeId, first.variantId || 0, first.cnCMeasureQuantityId || 0);
        }
      } else {
        setActiveScopeKey(null);
        activeScopeVariantIdRef.current = 0;
        cnCMeasureQuantityIdRef.current = 0;
        estimateIdRef.current = 0;
        setActiveEstimateId(0);
      }
      return;
    }

    // Initial page load — activate and load the first scope.
    const first = scopes[0];
    activeScopeVariantIdRef.current = first.variantId || 0;
    cnCMeasureQuantityIdRef.current = first.cnCMeasureQuantityId || 0;
    estimateIdRef.current = first.estimateId || 0;
    setActiveEstimateId(first.estimateId || 0);
    setActiveScopeKey(String(first.cnCMeasureQuantityId));
    loadEstimate(auth[0].token, contactInfoId, unitTypeId, first.variantId || 0, first.cnCMeasureQuantityId || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScopesLoaded]);

  const handleScopeTabSelect = (key) => {
    if (key === activeScopeKey) return;
    // Leaving the temp "New Estimate" tab — discard the pending state.
    if (pendingTab) setPendingTab(false);
    const scope = scopes.find((s) => String(s.cnCMeasureQuantityId) === key);
    if (!scope || !auth?.[0]?.token) return;
    activeScopeVariantIdRef.current = scope.variantId || 0;
    // Point the measure-quantity ref at the selected tab SYNCHRONOUSLY. Save/Update uses this ref to
    // target the right estimate; if we waited for the async load+hydrate to set it (and the backend
    // doesn't echo cnCMeasureQuantityId), Update would save to the PREVIOUS tab's estimate.
    cnCMeasureQuantityIdRef.current = scope.cnCMeasureQuantityId || 0;
    estimateIdRef.current = scope.estimateId || 0;
    setActiveEstimateId(scope.estimateId || 0);
    setActiveScopeKey(key);
    setFormKey((k) => k + 1);
    setInitialForm({ material: [], labor: [] });
    loadEstimate(auth[0].token, contactInfoId, unitTypeId, scope.variantId || 0, scope.cnCMeasureQuantityId || 0);
  };

  // When a saved estimate arrives, restore every entered value.
  useEffect(() => {
    if (isLoaded && estimate) {
      const hydrated = hydrateEstimate(estimate);
      estimateIdRef.current = hydrated.estimateId || 0;
      // Keep the id set by the active tab when the backend doesn't echo cnCMeasureQuantityId —
      // don't clobber it with 0, or Save/Update would lose track of which estimate is open.
      cnCMeasureQuantityIdRef.current = hydrated.cnCMeasureQuantityId || cnCMeasureQuantityIdRef.current || 0;
      setActiveEstimateId(hydrated.estimateId || 0);
      const mat = hydrated.materialList || [];
      const lab = hydrated.laborList || [];
      // Advance the scope block counter past the highest ID already in the loaded estimate so
      // the next Add Scope action gets a fresh, non-colliding scopeBlockId.
      const maxId = [...mat, ...lab].reduce((m, item) => Math.max(m, item.scopeBlockId || 0), 0);
      scopeBlockIdCounterRef.current = maxId;
      setInitialForm({ material: mat, labor: lab, scopePhotoUrl: hydrated.scopePhotoUrl || "" });
      setFormKey((k) => k + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, estimate]);

  // Success / failure notification on the save transition.
  useEffect(() => {
    const wasSaving = prevSavingRef.current;
    prevSavingRef.current = isSaving;
    if (wasSaving && !isSaving) {
      if (isSaved) {
        const savedId = hydrateEstimate(estimate).estimateId;
        if (savedId) estimateIdRef.current = savedId;

        // Tell the isScopesLoaded effect what to do when the tab refresh lands:
        //   'insert' → load + activate the first scope tab
        //   'update' → skip (current tab stays active; form reloaded below)
        afterSaveActionRef.current = wasInsertRef.current ? 'insert' : 'update';
        // Refresh top-level tabs so the new scopeName / estimateId appears.
        fetchScopes(auth[0].token, contactInfoId, unitTypeId);

        if (wasInsertRef.current) {
          // New estimate created (INSERT) — store its cnCMeasureQuantityId so isScopesLoaded
          // can activate and load that specific scope tab once GetScopes refreshes.
          afterInsertCnCIdRef.current = hydrateEstimate(estimate).cnCMeasureQuantityId || 0;
          setPendingTab(false);
          estimateIdRef.current = 0;
          scopeBlockIdCounterRef.current = 0;
          setActiveEstimateId(0);
          setInitialForm({ material: [], labor: [] });
          setFormKey((k) => k + 1);
          toast.success(`Estimate saved for ${floorPlanName}`);
        } else {
          // Existing estimate updated (UPDATE) — reload to confirm the saved state.
          setActiveEstimateId(savedId || 0);
          loadEstimate(auth[0].token, contactInfoId, unitTypeId, activeScopeVariantIdRef.current, cnCMeasureQuantityIdRef.current);
          toast.success(`Estimate updated for ${floorPlanName}`);
        }
      } else if (errors && Object.keys(errors).length > 0) {
        toast.error("Failed to save. Please try again.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaving]);

  const [convertToOrderOpen, setConvertToOrderOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteScope = async () => {
    const cnCId = cnCMeasureQuantityIdRef.current;
    if (!cnCId) return;
    setIsDeleting(true);
    try {
      await axios.delete(
        `${API_URL}/Ordering/api/Order/DeleteScope?CnCMeasureQuantityId=${cnCId}`,
        { headers: { Authorization: `Bearer ${auth?.[0]?.token}` } }
      );
      toast.success("Estimate deleted successfully.");
      afterSaveActionRef.current = 'delete';
      fetchScopes(auth[0].token, contactInfoId, unitTypeId);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data;
      toast.error(typeof msg === "string" ? msg : "Failed to delete estimate. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  // Add Estimate starts a new tab; Add Scope uses the same picker without resetting the
  // current estimate ID, tab, or edited rows.
  const handleAddEstimate = () => {
    if (!pendingTab) {
      // Not yet on a fresh temp tab — switch to one so new items never merge with an existing
      // estimate that may be loaded in the current form.
      setPendingTab(true);
      setActiveScopeKey("__new__");
      estimateIdRef.current = 0;
      cnCMeasureQuantityIdRef.current = 0;
      activeScopeVariantIdRef.current = 0;
      setActiveEstimateId(0);
      scopeBlockIdCounterRef.current = 0;
      setInitialForm({ material: [], labor: [] });
      setFormKey((k) => k + 1);
    }
    setAddScopeMode("estimate");
    setAddScopeOpen(true);
  };

  const handleAddScope = () => {
    if (!canEdit || isSaving || isEstimateLoading) return;
    setAddScopeMode("scope");
    setAddScopeOpen(true);
  };

  const handleScopeAdd = ({ material = [], labor = [] } = {}) => {
    if (material.length || labor.length) {
      // Group by the selected wizard step, not each kit item's catalog structure.
      const assigned = assignScopeBlocks({ material, labor }, scopeBlockIdCounterRef.current);
      scopeBlockIdCounterRef.current = assigned.lastBlockId;
      incomingTokenRef.current += 1;
      setIncoming({ material: assigned.material, labor: assigned.labor, token: incomingTokenRef.current });
    }
    setAddScopeOpen(false);
  };

  if (!isAuthenticated) return <Navigate to="/login" />;

  // Map productStructureId → structureName so reloaded items (which have productStructureId but
  // may lack structureName if the API doesn't persist it) still render in the correct scope card.
  const structureNameMap = {};
  (Array.isArray(ProductType?.valueList) ? ProductType.valueList : [])
    .filter((v) => v?.isCountertopCabinet === true)
    .forEach((v) => { structureNameMap[v.productStructureId] = v.productStructure; });

  // "Convert to Order" is only enabled when the SAVED estimate has all required fields.
  // Checked against initialForm (last backend-loaded data), not the live form state.
  const savedMat = (initialForm.material || []).map((item) => ({
    ...item,
    structureName: item.structureName || structureNameMap[item.productStructureId] || "",
  }));
  const savedLab = (initialForm.labor || []).map((item) => ({
    ...item,
    structureName: item.structureName || structureNameMap[item.productStructureId] || "",
  }));
  const savedGroups = groupByStructure(savedMat, savedLab);
  const savedScopeName =
    initialForm.material?.[0]?.scopeName || initialForm.labor?.[0]?.scopeName || "";
  const canConvertToOrder =
    activeEstimateId > 0 &&
    !!String(savedScopeName).trim() &&
    savedGroups.length > 0 &&
    savedGroups.every((g) => !!String(g.roomName || "").trim() && !!g.photoUrl);

  const handleSave = ({ materialList, laborList, scopeName = "", scopePhotoUrl = "" }) => {
    // Record whether this is an INSERT before dispatching — the save-success handler uses it.
    wasInsertRef.current = estimateIdRef.current === 0;
    const payload = buildEstimatePayload(
      { description: "", materialList, laborList, scopeName, scopePhotoUrl },
      {
        estimateId: estimateIdRef.current,
        cnCMeasureQuantityId: cnCMeasureQuantityIdRef.current,
        contactInfoId,
        storeId,
        unitTypeId,
        variantId: activeScopeVariantIdRef.current,
      }
    );
    console.log('--payload--', payload)
    saveEstimate(auth[0].token, payload);
  };

  return (
    <MainWrapper>
      {/* Bootstrap's centered, max-width `Container` (not `container-fluid`) — matches the left/right
          page margins used on the Order Confirmation screen instead of spanning edge-to-edge. */}
      <Container className="overflow-visible">
        <div className="sub-header mt-5 mb-3">
          <Title>Countertop / Cabinet Quantity Calculator</Title>
          <Link
            className="btn btn-primary me-1"
            to={hasRequiredParams ? `/customer-list/customer-detail/${contactInfoId}` : "/customer-list"}
          >
            <Icon icon={ic_arrow_back} />Back
          </Link>
        </div>

        {!canView ? (
          <div className="alert alert-danger" role="alert">
            You do not have permission to view this estimate.
          </div>
        ) : hasRequiredParams ? (
          <>
            <p className="text-muted">
              {propertyName} - floor plan {floorPlanName}
            </p>

            {canEdit && (
              <div className="mb-3">
                <CustomButton
                  variant="success"
                  type="button"
                  className="btn-add-scope"
                  handleClick={handleAddEstimate}
                  disabled={isSaving || isEstimateLoading || isScopesLoading}
                >
                  + Add Estimate
                </CustomButton>
              </div>
            )}

            {(scopes.length > 0 || pendingTab) && (
              <>
                <style>{`
                  .cc-page-scope-tabs .nav-link { color: #2D831F; border-color: #dee2e6 #dee2e6 #fff; background: #fff; }
                  .cc-page-scope-tabs .nav-link:hover { color: #1a5c10; }
                  .cc-page-scope-tabs .nav-link.active { background-color: #2D831F; color: #fff; border-color: #2D831F #2D831F #fff; font-weight: 600; }
                  .cc-page-scope-tabs .nav-link.pending-tab { font-style: italic; }
                `}</style>
                <Nav
                  variant="tabs"
                  className="cc-page-scope-tabs mb-3"
                  activeKey={activeScopeKey}
                  onSelect={handleScopeTabSelect}
                >
                  {scopes.map((scope, idx) => (
                    <Nav.Item key={scope.cnCMeasureQuantityId}>
                      <Nav.Link eventKey={String(scope.cnCMeasureQuantityId)}>
                        {(scope.scopeName || `Estimate ${idx + 1}`).replace(/\b\w/g, (c) => c.toUpperCase())}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                  {pendingTab && (
                    <Nav.Item key="__new__">
                      <Nav.Link eventKey="__new__" className="pending-tab">
                        New Estimate
                      </Nav.Link>
                    </Nav.Item>
                  )}
                </Nav>
              </>
            )}

            {(isSaving || isScopesLoading || isEstimateLoading) && (
              <div className="loading-container"><div className="spinner-border"></div></div>
            )}

            <ScopeEstimateForm
              key={formKey}
              initialMaterial={initialForm.material}
              initialLabor={initialForm.labor}
              initialScopePhotoUrl={initialForm.scopePhotoUrl || ""}
              isSaving={isSaving}
              isDeleting={isDeleting}
              isEditMode={activeEstimateId > 0}
              onSave={handleSave}
              onAddScope={handleAddScope}
              onConvertToOrder={() => setConvertToOrderOpen(true)}
              onDelete={handleDeleteScope}
              incomingItems={incoming}
              canEdit={canEdit}
              storeId={storeId}
              contactInfoId={contactInfoId}
              showCurrentCost={isCountertopCabinetCustomer}
              structureNameMap={structureNameMap}
              canConvertToOrder={canConvertToOrder}
              defaultScopeName={`Estimate ${scopes.length + 1}`}
            />
            {addScopeOpen ? (
              <AddScopeModal
                show={addScopeOpen}
                onClose={() => setAddScopeOpen(false)}
                onAdd={handleScopeAdd}
                storeId={storeId}
                contactInfoId={contactInfoId}
                title={addScopeMode === "scope" ? "Add Scope" : "Add Estimate"}
              />
            ) : null}
            {convertToOrderOpen ? (
              <ConvertToOrderModal
                show={convertToOrderOpen}
                onClose={() => setConvertToOrderOpen(false)}
                token={auth?.[0]?.token}
                contactInfoId={contactInfoId}
                unitTypeId={unitTypeId}
                cnCMeasureQuantityId={cnCMeasureQuantityIdRef.current}
                floorPlanName={floorPlanName}
                structureNameMap={structureNameMap}
                userId={auth?.[0]?.userId}
                uniqueChannelName={auth?.[0]?.uniqueChannelName || ""}
                salesPersonId={customerList?.[0]?.salesPersonId || null}
                requirePOToInstall={customerList?.[0]?.requirePOToInstall ?? false}
              />
            ) : null}
          </>
        ) : (
          <div className="alert alert-warning" role="alert">
            Missing customer or floor plan reference. Please return to the customer detail and
            select a floor plan from the Quantity Calculator.
          </div>
        )}
      </Container>
    </MainWrapper>
  );
};

const mapStateToProps = (state) => {
  const perms = deriveCountertopCabinetPermissions(state.Admin?.userMenuList);
  return {
    auth: state.auth,
    countertopCabinetEstimate: state.countertopCabinetEstimate,
    customerList: state.customerInfo?.customerList || [],
    storeInfo: state.StoreInfo?.storeInfo || [],
    ProductType: state.ProductType,
    canView: perms.canView,
    canEdit: perms.canEdit,
    scopes: state.countertopCabinetEstimate?.scopes || [],
    isScopesLoaded: state.countertopCabinetEstimate?.isScopesLoaded || false,
    isScopesLoading: state.countertopCabinetEstimate?.isScopesLoading || false,
    isEstimateLoading: state.countertopCabinetEstimate?.isLoading || false,
  };
};

const mapDispatchToProps = (dispatch) => ({
  fetchCustomerList: (token, sort, page, paginate, listParamState, userId, salesPersonId, GrpPcId, isChecked) =>
    dispatch({ type: CUSTOMER_LIST_REQUEST, token, sort, page, paginate, listParamState, userId, salesPersonId, GrpPcId, isChecked }),
  fetchScopes: (token, contactInfoId, unitTypeId) =>
    dispatch({ type: CC_SCOPES_REQUEST, token, contactInfoId, unitTypeId }),
  saveEstimate: (token, payload) => dispatch({ type: CC_ESTIMATE_SAVE_REQUEST, token, payload }),
  loadEstimate: (token, contactInfoId, unitTypeId, variantId, cnCMeasureQuantityId = 0) =>
    dispatch({ type: CC_ESTIMATE_LOAD_REQUEST, token, contactInfoId, unitTypeId, variantId, cnCMeasureQuantityId }),
  fetchStoreInfo: (token) => dispatch({ type: STORE_INFO_REQUEST, token }),
  fetchProductType: (token) => dispatch({ type: PRODUCT_TYPE_REQUEST, token }),
});

export default connect(mapStateToProps, mapDispatchToProps)(CountertopCabinetQuantity);
